/**
 * SensibilisationVoiturette.jsx — Séquence 5 rendue avec la trame du module
 * sensibilisation cyclo (accroche + cards + scénarios + checklist), mais
 * contenu 100% adapté au quadricycle léger à moteur.
 *
 * Distinction visuelle : bandeau BLEU en tête = référentiel officiel des
 * 7 thèmes ; puis renderer enrichi (rouge/amber) pour la version pédagogique.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import {
  Home, ArrowLeft, ShieldAlert, Sparkles, CheckCircle2, HelpCircle,
  ChevronDown, ChevronUp, Users, AlertTriangle,
} from 'lucide-react';
import useSeo from '../../../hooks/useSeo';
import { RefOfficiel, RefList } from '../../../components/RefBlock';
import { SEQUENCES } from '../../../data/cours/permisAmVoiturette/referentiel';
import { VISUELS } from '../../../data/cours/permisAmVoiturette/visuels';
import { VisuelCard } from '../../../components/voiturette/VisuelCard';
import { SafeVideoEmbed } from '../../../components/SafeVideoEmbed';
import { ProjectorToggle } from '../../../components/ProjectorToggle';

const SENSIBILISATION_VIDEOS = [
  { videoId: 'chTLzBlEtzE', title: 'Sensibilisation Permis AM — vidéo officielle', channel: 'Sécurité routière' },
  { videoId: 'm6jPvzGomnA', title: 'Sensibilisation Permis AM — témoignage / campagne', channel: 'Sécurité routière' },
];

/* Bloc « À retenir » — repris de la trame cyclo */
function Retenir({ text }) {
  if (!text) return null;
  return (
    <div className="mt-6 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-600/10 via-amber-500/5 to-transparent p-4 sm:p-5 flex items-start gap-3">
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-amber-400" />
      </div>
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1">À retenir</div>
        <p className="text-sm sm:text-base font-black leading-snug text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {text}
        </p>
      </div>
    </div>
  );
}

/* Scénario interactif — même pattern que le module cyclo */
function ScenarioCard({ scenario }) {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(null);
  return (
    <div
      className={`rounded-2xl border-2 transition-all overflow-hidden ${
        expanded ? 'border-amber-500/60 bg-gradient-to-br from-amber-600/15 to-transparent' : 'border-white/10 bg-black/20 hover:border-amber-500/40'
      }`}
      data-testid={`scenario-${scenario.id}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 sm:p-5"
        aria-expanded={expanded}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1 w-10 h-10 rounded-full bg-amber-500/15 border-2 border-amber-500/40 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-1">Que ferais-tu ?</div>
            <h4 className="text-base sm:text-lg font-black leading-tight text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {scenario.title}
            </h4>
            <p className="text-sm text-muted-foreground mt-1">{scenario.question}</p>
          </div>
          <div className="flex-shrink-0 mt-1">
            {expanded ? <ChevronUp className="w-5 h-5 text-amber-400" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5 space-y-2">
          {scenario.reponses.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSelected(i)}
              className={`w-full text-left px-3 py-2.5 rounded-lg border transition ${
                selected === i
                  ? r.bon
                    ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-100'
                    : 'bg-rose-500/10 border-rose-500/40 text-rose-100'
                  : 'bg-black/30 border-white/10 text-white/85 hover:bg-white/[0.06]'
              }`}
              data-testid={`scenario-${scenario.id}-option-${i}`}
            >
              <div className="flex items-start gap-2">
                {selected === i && (r.bon ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-300 shrink-0" />)}
                <div className="flex-1">
                  <div className="text-[13px] font-semibold">{r.txt}</div>
                  {selected === i && <div className="text-[11.5px] mt-1 italic opacity-85">{r.feedback}</div>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionRenderer({ section }) {
  const { num, kicker, title, lead, type, list, cards, scenarios, checklist, retenir } = section;
  return (
    <section id={`section-${num}`} className="scroll-mt-20">
      <div className="mb-4">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-black uppercase tracking-widest text-amber-400 mb-2">
          <span className="text-amber-300">{num}</span> · {kicker}
        </div>
        <h3 className="text-2xl sm:text-3xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {title}
        </h3>
        {lead && <p className="mt-2 text-white/80 leading-relaxed">{lead}</p>}
      </div>

      {type === 'list' && list && (
        <ul className="space-y-2">
          {list.map((it, i) => (
            <li key={i} className="rounded-xl bg-black/25 border border-white/10 p-3 sm:p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-300 mb-1">{it.label}</div>
              <div className="text-white/85 text-[13px]">{it.desc}</div>
            </li>
          ))}
        </ul>
      )}

      {type === 'cards3' && cards && (
        <div className="grid sm:grid-cols-3 gap-3">
          {cards.map((c, i) => (
            <div key={i} className="rounded-xl bg-black/30 border border-amber-500/25 p-4">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-300 mb-1">{c.title}</div>
              <div className="text-white/85 text-[13px]">{c.desc}</div>
            </div>
          ))}
        </div>
      )}

      {type === 'scenarios' && scenarios && (
        <div className="space-y-3">
          {scenarios.map((sc) => <ScenarioCard key={sc.id} scenario={sc} />)}
        </div>
      )}

      {type === 'checklist' && checklist && (
        <ul className="space-y-2" data-testid={`section-${num}-checklist`}>
          {checklist.map((c, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl bg-black/25 border border-emerald-500/25 p-3">
              <input type="checkbox" className="mt-1 w-4 h-4 accent-emerald-500" data-testid={`section-${num}-check-${i}`} />
              <span className="text-white/90 text-[13px]">{c}</span>
            </li>
          ))}
        </ul>
      )}

      <Retenir text={retenir} />
    </section>
  );
}

export default function SensibilisationVoiturette({ meta, data }) {
  const enrichi = data.enrichi;
  useSeo({
    title: `${meta.kicker} · ${enrichi.title} — Voiturette Code 2 Roues`,
    description: enrichi.subtitle,
  });

  return (
    <div className="min-h-screen bg-background text-foreground pb-20" data-testid="sensibilisation-voiturette">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="outline" size="sm">
            <Link to="/permis-am/voiturette" aria-label="Retour au Hub Voiturette">
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Hub Voiturette</span>
            </Link>
          </Button>
          <div className="flex-1 text-center min-w-0">
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-amber-400 truncate whitespace-nowrap" title={`Séquence 5 · Sensibilisation · ${meta.duree}`}>
              <span className="sm:hidden">S5 · SENSIBILISATION · {meta.duree}</span>
              <span className="hidden sm:inline">SÉQUENCE 5 · SENSIBILISATION · {meta.duree}</span>
            </div>
          </div>
          <ProjectorToggle testid="proj-sensibilisation-voiturette" />
          <Button asChild variant="ghost" size="sm">
            <Link to="/" aria-label="Retour à l'accueil">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Accueil</span>
            </Link>
          </Button>
        </div>
      </div>

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Hero */}
        <header className="mb-8 lg:mb-14 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-black uppercase tracking-widest text-amber-400 mb-4">
            <ShieldAlert className="w-3 h-3" /> Séquence 5 · Voiturette
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.05] tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {enrichi.title}
          </h1>
          <p className="text-lg lg:text-xl text-amber-400 font-black tracking-wide mb-6">{enrichi.subtitle}</p>
          <div className="max-w-2xl mx-auto p-4 lg:p-6 rounded-xl border border-amber-500/30 bg-amber-500/5">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-2">Fil conducteur</div>
            <div className="text-sm md:text-base lg:text-lg font-black text-foreground leading-snug" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {enrichi.mantra}
            </div>
          </div>
          <p className="mt-6 text-sm lg:text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {enrichi.intro}
          </p>
        </header>

        {/* Visuel 12 — synthèse des risques du quotidien */}
        <div className="mb-8">
          <VisuelCard visuel={VISUELS[12]} testid="s5-visuel-risques-quotidien" />
        </div>

        {/* Bloc officiel — les 7 thèmes réglementaires */}
        <div className="mb-8">
          <RefOfficiel
            title="Les 7 thèmes officiels"
            subtitle={data.officiel.regle}
            duration={meta.duree}
            testid="s5-off-themes"
          >
            <p className="italic text-sky-100/85">{data.officiel.parentPresent}</p>
            <ol className="mt-2 space-y-1.5">
              {data.officiel.themes.map((t) => (
                <li key={t.key} className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-sky-500/25 border border-sky-500/40 text-sky-200 flex items-center justify-center text-[11px] font-black shrink-0">
                    {t.num}
                  </span>
                  <div>
                    <div className="text-white font-black text-[13px]">{t.label}</div>
                    <div className="text-white/70 text-[12px]">{t.desc}</div>
                  </div>
                </li>
              ))}
            </ol>
          </RefOfficiel>
        </div>

        {/* Sommaire enrichi */}
        <nav className="mb-8 p-4 rounded-xl border border-white/10 bg-black/20">
          <div className="text-[11px] font-black uppercase tracking-widest text-amber-400 mb-3">Sommaire · {enrichi.sections.length} sections enrichies</div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {enrichi.sections.map((s) => (
              <a key={s.num} href={`#section-${s.num}`} className="text-sm text-foreground/80 hover:text-amber-400 transition py-0.5">
                <span className="font-black text-amber-500/60 mr-2">{s.num}</span>{s.title}
              </a>
            ))}
          </div>
        </nav>

        <div className="space-y-10">
          {enrichi.sections.map((s) => <SectionRenderer key={s.num} section={s} />)}
        </div>

        {/* Vidéos officielles de sensibilisation (RGPD-safe) */}
        <section
          className="mt-14 p-6 md:p-8 rounded-2xl border-2 border-red-500/30 bg-gradient-to-br from-red-600/10 via-amber-500/5 to-transparent"
          data-testid="voiturette-videos-sensibilisation"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400">Vidéos officielles</div>
              <h3 className="text-lg md:text-xl font-black text-foreground leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                À visionner ensemble — ancrer les messages avant de conduire seul
              </h3>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-5 max-w-3xl">
            Ces vidéos officielles complètent la séquence de sensibilisation. Intégration <strong>youtube-nocookie</strong> :
            aucune donnée n'est envoyée à YouTube tant que tu n'as pas lancé la lecture.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {SENSIBILISATION_VIDEOS.map((v) => (
              <SafeVideoEmbed
                key={v.videoId}
                videoId={v.videoId}
                title={v.title}
                channel={v.channel}
                testid={`video-voiturette-sensibilisation-${v.videoId}`}
              />
            ))}
          </div>
        </section>

        {/* Navigation retour hub */}
        <div className="mt-12 p-6 rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-600/15 to-transparent text-center">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-400 mb-2">Formation terminée</div>
          <h3 className="text-xl md:text-2xl font-black text-foreground mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Bravo — tu as parcouru les 5 séquences officielles
          </h3>
          <div className="flex flex-wrap justify-center gap-2">
            <Button asChild className="bg-amber-600 hover:bg-amber-500 text-white">
              <Link to="/permis-am/voiturette">Revenir au hub Voiturette</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/permis-am/voiturette/${SEQUENCES[3].slug}`}>Retour Séquence 4 · Circulation</Link>
            </Button>
          </div>
        </div>
      </article>
    </div>
  );
}
